import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { WallPost, wallPostsApi } from '../../../api/wallPostsApi';
import { useNavigation } from '../../../shared/contexts/NavigationContext';
import Post from './Post';
import EditPostModal from './EditPostModal';
import styles from './PostsFeed.module.css';

interface PostsFeedProps {
  posts: WallPost[]; // Начальные посты (включая пост в фокусе)
  initialPostId: number; // ID поста, к которому нужно проскроллить
  onClose: () => void;
  onLike: (postId: number) => void;
  onDelete: (postId: number) => void;
  currentUserId: number;
  onUserClick?: (userId: number) => void;
  onSiteClick?: (siteId: number) => void; // Клик по объекту в шапке поста
  siteName?: string | null; // Название объекта для заголовка
  hideMenuButton?: boolean;
  showBackButton?: boolean;
  // Параметры для динамической загрузки постов
  loadPostsUserId?: number | null; // ID пользователя для загрузки постов (null = все посты)
  loadPostsSiteId?: number | null; // ID объекта для фильтрации постов
}

const PostsFeed: React.FC<PostsFeedProps> = ({
  posts: initialPosts,
  initialPostId,
  onClose,
  onLike,
  onDelete,
  currentUserId,
  onUserClick,
  onSiteClick,
  siteName,
  hideMenuButton,
  showBackButton = true,
  loadPostsUserId,
  loadPostsSiteId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const postRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const { pushBackLayer, popBackLayer, hideMenu, state } = useNavigation();
  const isFeedOpenRef = useRef(true);
  const [allPosts, setAllPosts] = useState<WallPost[]>(initialPosts);
  const [editingPost, setEditingPost] = useState<WallPost | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreBefore, setHasMoreBefore] = useState(true);
  const [hasMoreAfter, setHasMoreAfter] = useState(true);
  const loadingRef = useRef(false);

  // Находим индекс поста в фокусе
  const focusedPostIndex = useMemo(() => {
    return allPosts.findIndex(post => post.id === initialPostId);
  }, [allPosts, initialPostId]);

  // Находим самый старый и самый новый пост для определения границ загрузки
  const oldestPostDate = useMemo(() => {
    if (allPosts.length === 0) return null;
    return Math.min(...allPosts.map(p => new Date(p.createdAt).getTime()));
  }, [allPosts]);

  const newestPostDate = useMemo(() => {
    if (allPosts.length === 0) return null;
    return Math.max(...allPosts.map(p => new Date(p.createdAt).getTime()));
  }, [allPosts]);

  const beforeOffsetRef = useRef(0);
  const afterOffsetRef = useRef(0);

  // Загружаем посты до поста в фокусе (более старые)
  // API возвращает посты отсортированные по CreatedAt DESC (новые сверху)
  // Поэтому для загрузки старых постов нужно увеличивать offset
  const loadPostsBefore = useCallback(async (silent = false) => {
    if (loadingRef.current || !hasMoreBefore || !oldestPostDate) return;

    loadingRef.current = true;
    if (!silent) setIsLoadingMore(true);

    try {
      const batchSize = 20;
      let batch: WallPost[];
      
      if (loadPostsUserId !== undefined && loadPostsUserId !== null) {
        batch = await wallPostsApi.getUserPosts(loadPostsUserId, batchSize, beforeOffsetRef.current, loadPostsSiteId);
      } else {
        batch = await wallPostsApi.getAllPosts(batchSize, beforeOffsetRef.current);
      }

      if (batch.length === 0) {
        setHasMoreBefore(false);
        return;
      }

      // Фильтруем посты, которые старше самого старого поста и еще не загружены
      const olderPosts = batch
        .filter(p => {
          const postDate = new Date(p.createdAt).getTime();
          return postDate < oldestPostDate && !allPosts.some(existing => existing.id === p.id);
        });

      if (olderPosts.length > 0) {
        // Добавляем новые посты в начало (они старше)
        setAllPosts(prev => [...olderPosts, ...prev]);
        beforeOffsetRef.current += batchSize;
      } else {
        // Если нет постов старше в этой порции, увеличиваем offset
        beforeOffsetRef.current += batchSize;
        // Если загрузили меньше, чем запросили, значит больше нет
        if (batch.length < batchSize) {
          setHasMoreBefore(false);
        }
      }

      // Если загрузили меньше, чем запросили, значит больше нет
      if (batch.length < batchSize) {
        setHasMoreBefore(false);
      }
    } catch (error) {
      console.error('Ошибка загрузки постов до поста в фокусе:', error);
      setHasMoreBefore(false);
    } finally {
      if (!silent) setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [allPosts, oldestPostDate, loadPostsUserId, loadPostsSiteId, hasMoreBefore]);

  // Загружаем посты после поста в фокусе (более новые)
  // API возвращает посты отсортированные по CreatedAt DESC (новые сверху)
  // Поэтому для загрузки новых постов нужно начинать с offset=0 и искать посты новее
  const loadPostsAfter = useCallback(async (silent = false) => {
    if (loadingRef.current || !hasMoreAfter || !newestPostDate) return;

    loadingRef.current = true;
    if (!silent) setIsLoadingMore(true);

    try {
      const batchSize = 20;
      let batch: WallPost[];
      
      // Для новых постов начинаем с начала (offset=0) и ищем посты новее
      if (loadPostsUserId !== undefined && loadPostsUserId !== null) {
        batch = await wallPostsApi.getUserPosts(loadPostsUserId, batchSize, afterOffsetRef.current, loadPostsSiteId);
      } else {
        batch = await wallPostsApi.getAllPosts(batchSize, afterOffsetRef.current);
      }

      if (batch.length === 0) {
        setHasMoreAfter(false);
        return;
      }

      // Фильтруем посты, которые новее самого нового поста и еще не загружены
      const newerPosts = batch
        .filter(p => {
          const postDate = new Date(p.createdAt).getTime();
          return postDate > newestPostDate && !allPosts.some(existing => existing.id === p.id);
        });

      if (newerPosts.length > 0) {
        // Добавляем новые посты в конец (они новее)
        setAllPosts(prev => [...prev, ...newerPosts]);
        afterOffsetRef.current += batchSize;
      } else {
        // Если нет постов новее в этой порции, увеличиваем offset
        afterOffsetRef.current += batchSize;
        // Если загрузили меньше, чем запросили, значит больше нет
        if (batch.length < batchSize) {
          setHasMoreAfter(false);
        }
      }

      // Если загрузили меньше, чем запросили, значит больше нет
      if (batch.length < batchSize) {
        setHasMoreAfter(false);
      }
    } catch (error) {
      console.error('Ошибка загрузки постов после поста в фокусе:', error);
      setHasMoreAfter(false);
    } finally {
      if (!silent) setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [allPosts, newestPostDate, loadPostsUserId, loadPostsSiteId, hasMoreAfter]);

  // Подгружаем посты после окончания анимации открытия, без индикатора загрузки (чтобы лента не прыгала)
  useEffect(() => {
    if (!oldestPostDate || !newestPostDate) return;
    const t = setTimeout(() => {
      loadPostsBefore(true);
      loadPostsAfter(true);
    }, 380);
    return () => clearTimeout(t);
  }, [oldestPostDate, newestPostDate, loadPostsBefore, loadPostsAfter]);

  // Сортируем посты: выбранный при открытии пост — первым (сразу в фокусе без скролла), остальные по дате
  const sortedPosts = useMemo(() => {
    const byDate = [...allPosts].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });
    const focusIndex = byDate.findIndex((p) => p.id === initialPostId);
    if (focusIndex <= 0) return byDate;
    const focused = byDate[focusIndex];
    const rest = byDate.filter((_, i) => i !== focusIndex);
    return [focused, ...rest];
  }, [allPosts, initialPostId]);

  // Вешаем закрытие ленты поверх текущего слоя «назад» (pushBackLayer), при размонтировании снимаем (popBackLayer), чтобы не скрывать стрелку родителя (профиль / окно объекта)
  useEffect(() => {
    if (!showBackButton) return;

    isFeedOpenRef.current = true;
    pushBackLayer(onClose, true);
    hideMenu();
    return () => {
      isFeedOpenRef.current = false;
      popBackLayer();
    };
  }, [pushBackLayer, popBackLayer, hideMenu, onClose, showBackButton]);

  // Восстанавливаем слой «назад» ленты, если его снял кто-то другой (например, ImageViewer)
  useEffect(() => {
    if (!showBackButton) return;
    if (isFeedOpenRef.current && !state.back.show) {
      const timeoutId = setTimeout(() => {
        if (isFeedOpenRef.current) {
          pushBackLayer(onClose, true);
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [state.back.show, pushBackLayer, onClose, showBackButton]);

  // Сохраняем ref для поста
  const setPostRef = useCallback((postId: number, element: HTMLDivElement | null) => {
    if (element) {
      postRefs.current.set(postId, element);
    } else {
      postRefs.current.delete(postId);
    }
  }, []);

  // Блокируем скролл body после первого кадра, чтобы не было рывка при открытии
  useEffect(() => {
    const scrollY = window.scrollY;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalWidth = document.body.style.width;

    const applyLock = () => {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    };
    const id = requestAnimationFrame(applyLock);

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.width = originalWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const modalContent = (
    <div className={styles.feedOverlay}>
      {/* Заголовок с собственной кнопкой назад (работает и в боттомшите, где глобальная «назад» недоступна) */}
      <div className={styles.feedHeader}>
        {showBackButton ? (
          <button
            type="button"
            className={styles.feedBackButton}
            onClick={onClose}
            aria-label="Закрыть"
            title="Назад"
          >
            <i className="ri-arrow-left-s-line" aria-hidden />
          </button>
        ) : (
          <div className={styles.backButtonSpacer} />
        )}
        <h2 className={styles.feedTitle}>
          {siteName ? siteName : 'Публикации'}
        </h2>
        <div className={styles.headerSpacer}></div>
      </div>
      
      {/* Контейнер с постами */}
      <div 
        ref={containerRef}
        className={styles.feedContainer}
        onScroll={(e) => {
          const target = e.target as HTMLDivElement;
          const scrollTop = target.scrollTop;
          const scrollHeight = target.scrollHeight;
          const clientHeight = target.clientHeight;
          
          // Загружаем посты при скролле вверх (старые посты)
          if (scrollTop < 500 && hasMoreBefore && !loadingRef.current) {
            loadPostsBefore();
          }
          
          // Загружаем посты при скролле вниз (новые посты)
          if (scrollTop + clientHeight > scrollHeight - 500 && hasMoreAfter && !loadingRef.current) {
            loadPostsAfter();
          }
        }}
      >
        <div className={styles.postsContainer}>
          {isLoadingMore && (
            <div className={styles.loadingIndicator}>
              <i className="ri-loader-4-line"></i>
            </div>
          )}
          {sortedPosts.map((post, index) => (
            <div 
              key={post.id}
              ref={(el) => setPostRef(post.id, el)}
              className={styles.postWrapper}
            >
              <Post
                post={post}
                onLike={onLike}
                onDelete={onDelete}
                onEdit={p => setEditingPost(p)}
                currentUserId={currentUserId}
                onUserClick={onUserClick}
                onSiteClick={onSiteClick}
                hideMenuButton={hideMenuButton}
              />
              {index < sortedPosts.length - 1 && (
                <hr className={styles.postDivider} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {typeof document !== 'undefined'
        ? createPortal(modalContent, document.body)
        : null}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onUpdated={updated => {
            setAllPosts(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingPost(null);
          }}
        />
      )}
    </>
  );
};

export default PostsFeed;
